import { modificaUtenteSchema, type UtenteDettaglio as Dettaglio } from '@aulaviva/shared';
import { Alert, Badge, Button, Card, DataTable, Dialog, EmptyState, TextArea, TextField, useToast, type ColumnDef } from '@aulaviva/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarX, Pencil, ShieldCheck, ShieldOff, UserCheck, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useModificaUtente, useSospensioneUtente, useUtenteDettaglio } from '@/api/amministrazione';
import { formattaDataBreve, formattaDataOra } from '@/lib/date';
import { admin, amministrazione, comuni } from '@/testi';
import { IntestazioneAdmin, LinkIndietro } from './comuni';

const t = amministrazione.utente;
type RigaWebinar = Dettaglio['webinar'][number];
type FormModifica = { nome: string; cognome: string; email: string; ordine?: string; numeroAlbo?: string; motivo: string };

export function UtenteDettaglio() {
  const { id = '' } = useParams();
  const toast = useToast();
  const utente = useUtenteDettaglio(id);
  const modifica = useModificaUtente(id);
  const sospensione = useSospensioneUtente(id);
  const [dialogo, setDialogo] = useState<'modifica' | 'sospendi' | 'riattiva' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);

  const colonne = useMemo<ColumnDef<RigaWebinar, unknown>[]>(
    () => [
      { header: t.colonne.webinar, accessorKey: 'titolo' },
      { header: t.colonne.data, cell: ({ row }) => formattaDataBreve(row.original.inizioPrevisto) },
      { header: t.colonne.conferma, cell: ({ row }) => (row.original.conferma ? (admin.statiConferma as Record<string, string>)[row.original.conferma] ?? row.original.conferma : '—') },
      { header: t.colonne.modalita, cell: ({ row }) => (row.original.modalita ? (admin.modalita as Record<string, string>)[row.original.modalita] ?? row.original.modalita : '—') },
      { header: t.colonne.minuti, cell: ({ row }) => (row.original.minutiTotali ?? '—') },
      { header: t.colonne.percentuale, cell: ({ row }) => (row.original.percentuale === null ? '—' : `${Math.round(row.original.percentuale)}%`) },
      { header: t.colonne.test, cell: ({ row }) => (row.original.esitoTest ? t.esitiTest[row.original.esitoTest] ?? row.original.esitoTest : '—') },
      { header: t.colonne.attestato, cell: ({ row }) => (row.original.attestatoCodice ? <span className="t-code text-[13px]">{row.original.attestatoCodice}</span> : '—') },
    ],
    [],
  );

  if (utente.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (utente.isError) return <Alert variant="error" title={utente.error.message} action={<Button onClick={() => utente.refetch()}>{comuni.riprova}</Button>} />;
  const u = utente.data;
  const nome = `${u.nome} ${u.cognome}`;

  async function confermaSospensione(azione: 'sospendi' | 'riattiva') {
    setErrore(null);
    if (motivo.trim().length < 3) {
      setErrore(t.motivoAiuto);
      return;
    }
    try {
      await sospensione.mutateAsync({ azione, motivo: motivo.trim() });
      setDialogo(null);
      setMotivo('');
      toast.mostra({ variant: 'success', title: azione === 'sospendi' ? t.sospeso : t.riattivato });
    } catch (e) {
      setErrore(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  return (
    <>
      <IntestazioneAdmin
        sopra={<LinkIndietro to="/admin/utenti" testo={t.torna} />}
        titolo={
          <span className="inline-flex flex-wrap items-center gap-3">
            {nome}
            <Badge variant={u.stato === 'attivo' ? 'success' : u.stato === 'sospeso' ? 'error' : 'warning'} icon={u.stato === 'sospeso' ? UserX : UserCheck}>
              {admin.statiUtente[u.stato]}
            </Badge>
          </span>
        }
        sottotitolo={`${admin.ruoli[u.ruolo]} · ${u.email}`}
        azione={
          <div className="flex flex-wrap gap-3">
            <Button icon={Pencil} onClick={() => setDialogo('modifica')}>
              {t.modifica}
            </Button>
            {u.stato === 'sospeso' ? (
              <Button variant="primary" icon={UserCheck} onClick={() => { setDialogo('riattiva'); setMotivo(''); setErrore(null); }}>
                {t.riattiva}
              </Button>
            ) : (
              <Button icon={UserX} onClick={() => { setDialogo('sospendi'); setMotivo(''); setErrore(null); }}>
                {t.sospendi}
              </Button>
            )}
          </div>
        }
      />
      <Card>
        <h2 className="t-title-3 mb-4">{t.anagrafica}</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-8 gap-y-3">
          <dt className="t-label text-ink-muted">{t.codiceFiscale}</dt>
          <dd className="flex flex-col gap-1">
            <span className="t-code">{u.codiceFiscale}</span>
            <span className="t-body-small text-ink-muted">{t.codiceFiscaleAiuto}</span>
          </dd>
          <dt className="t-label text-ink-muted">{admin.utenti.colonne.email}</dt>
          <dd className="t-body">{u.email}</dd>
          <dt className="t-label text-ink-muted">{admin.percorsi.iscritti.colonne.ordine}</dt>
          <dd className="t-body">{u.ordine ?? '—'}{u.numeroAlbo ? ` · n. ${u.numeroAlbo}` : ''}</dd>
          <dt className="t-label text-ink-muted">{t.totp}</dt>
          <dd>
            <Badge variant={u.totpAttivo ? 'success' : 'neutral'} icon={u.totpAttivo ? ShieldCheck : ShieldOff}>
              {u.totpAttivo ? t.totpAttivo : t.totpNonAttivo}
            </Badge>
          </dd>
          <dt className="t-label text-ink-muted">{t.ultimoAccesso}</dt>
          <dd className="t-body">{u.ultimoAccessoAt ? formattaDataOra(u.ultimoAccessoAt) : t.mai}</dd>
          <dt className="t-label text-ink-muted">{t.creato}</dt>
          <dd className="t-body">{formattaDataOra(u.createdAt)}</dd>
        </dl>
      </Card>
      <section className="flex flex-col gap-4">
        <h2 className="t-title-2">{t.storico}</h2>
        <Card className="flex flex-col gap-2">
          <h3 className="t-title-3">{t.iscrizioni}</h3>
          {u.iscrizioni.length === 0 ? (
            <p className="t-body text-ink-muted">{t.nessunaIscrizione}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {u.iscrizioni.map((i) => (
                <li key={i.percorsoId} className="t-body">
                  {i.percorsoTitolo} <span className="text-ink-muted">· {i.stato} · {formattaDataBreve(i.iscrittoDal)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <DataTable
          caption={t.webinar}
          columns={colonne}
          data={u.webinar}
          getRowId={(w) => w.webinarId}
          testi={admin.comuni}
          empty={<EmptyState icon={CalendarX} title={t.nessunWebinar} />}
        />
      </section>
      {dialogo === 'modifica' && (
        <DialogoModifica
          utente={u}
          loading={modifica.isPending}
          onChiudi={() => setDialogo(null)}
          onSalva={async (dati) => {
            await modifica.mutateAsync(dati);
            setDialogo(null);
            toast.mostra({ variant: 'success', title: t.salvato });
          }}
        />
      )}
      <Dialog
        open={dialogo === 'sospendi' || dialogo === 'riattiva'}
        onOpenChange={(o) => !o && setDialogo(null)}
        title={dialogo === 'sospendi' ? t.sospendiTitolo(nome) : t.riattivaTitolo(nome)}
        description={dialogo === 'sospendi' ? t.sospendiTesto : t.riattivaTesto}
        actions={
          <>
            <Button onClick={() => setDialogo(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={() => confermaSospensione(dialogo === 'sospendi' ? 'sospendi' : 'riattiva')} loading={sospensione.isPending} loadingText={admin.comuni.inCorso}>
              {dialogo === 'sospendi' ? t.sospendi : t.riattiva}
            </Button>
          </>
        }
      >
        {errore && <Alert variant="error" title={errore} />}
        <TextArea label={t.motivoSospensione} hint={t.motivoAiuto} value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={300} />
      </Dialog>
    </>
  );
}

function DialogoModifica({ utente, loading, onChiudi, onSalva }: { utente: Dettaglio; loading: boolean; onChiudi: () => void; onSalva: (dati: FormModifica) => Promise<void> }) {
  const [errore, setErrore] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<FormModifica>({
    resolver: zodResolver(modificaUtenteSchema),
    defaultValues: { nome: utente.nome, cognome: utente.cognome, email: utente.email, ordine: utente.ordine ?? '', numeroAlbo: utente.numeroAlbo ?? '', motivo: '' },
  });
  async function invia(dati: FormModifica) {
    setErrore(null);
    try {
      await onSalva(dati);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onChiudi()}
      title={t.modificaTitolo}
      description={t.modificaTesto}
      actions={
        <>
          <Button onClick={onChiudi}>{admin.comuni.annulla}</Button>
          <Button variant="primary" type="submit" form="form-utente" loading={loading} loadingText={admin.comuni.inCorso}>
            {t.salva}
          </Button>
        </>
      }
    >
      <form id="form-utente" onSubmit={handleSubmit(invia)} noValidate className="flex flex-col gap-4">
        {errore && <Alert variant="error" title={errore} />}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label={admin.percorsi.iscritti.colonne.nome} error={formState.errors.nome?.message} {...register('nome')} />
          <TextField label="Cognome" error={formState.errors.cognome?.message} {...register('cognome')} />
        </div>
        <TextField label={admin.utenti.colonne.email} type="email" error={formState.errors.email?.message} {...register('email')} />
        <TextField label={admin.percorsi.iscritti.colonne.ordine} error={formState.errors.ordine?.message} {...register('ordine')} />
        <TextField label="Numero di iscrizione all’albo" error={formState.errors.numeroAlbo?.message} {...register('numeroAlbo')} />
        <TextArea label={t.motivo} hint={t.motivoAiuto} rows={2} error={formState.errors.motivo?.message} {...register('motivo')} />
      </form>
    </Dialog>
  );
}
